import { sql } from "drizzle-orm";
import { index, pgTable, text, uniqueIndex, uuid } from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";

import { patientsTable } from "./patientsSchema";
import { customTimestamp } from "./utils/types";

export const patientShortCodesTable = pgTable(
  "patient_short_codes",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    patientId: uuid("patient_id")
      .references(() => patientsTable.id, { onDelete: "cascade" })
      .notNull(),
    shortCode: text("short_code").notNull(),
    entryPointType: text("entry_point_type"),
    createdAt: customTimestamp("created_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
    updatedAt: customTimestamp("updated_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString())
      .$onUpdateFn(() => new Date().toISOString()),
  },
  table => [
    index("patient_shortcodes_patient_id_index").on(table.patientId),
    // Single-use for every non-QR code. QR codes are reusable across accounts, so
    // they are excluded from this index. `IS DISTINCT FROM` keeps legacy NULL rows
    // (all pre-QR-storage, i.e. non-QR) covered — a plain `<> 'QR_CODE'` would drop
    // them from the index and silently lose their uniqueness guarantee.
    uniqueIndex("patient_short_codes_short_code_non_qr_unique")
      .on(table.shortCode)
      .where(sql`${table.entryPointType} IS DISTINCT FROM 'QR_CODE'`),
    // A patient never has the same code twice (incl. reusable QR codes) — makes the
    // assign flow's re-scan idempotent and gives the race path a stable constraint.
    uniqueIndex("patient_short_codes_patient_id_short_code_unique").on(
      table.patientId,
      table.shortCode,
    ),
  ],
);

export const insertPatientShortCodesSchema = createInsertSchema(
  patientShortCodesTable,
  {
    patientId: z.uuid().min(1),
    shortCode: z.string().min(1),
  },
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  entryPointType: true,
});

export type InsertPatientShortCodesType = z.infer<
  typeof insertPatientShortCodesSchema
>;

export const patientShortCodesSchema = createSelectSchema(
  patientShortCodesTable,
);
export type PatientShortCodesType = z.infer<typeof patientShortCodesSchema>;
